import type { PageComponentPropsWithComponent } from '@/features/a-dynamic-page/components/page-component-switch';
import type {
    SpotResultDesktopV4Meta,
    SpotResultsDesktopV4_jsonld_page_read,
    SpotResultsTileDesktopAttributes,
} from '@/features/spot-results/spot-results-desktop-v4/spot-results-desktop-v4-type';

import React from 'react';

import logger from '@app/core-features/logger/logger';

import { InView, LazyComponent } from '@/core/features';
import useComponentPagination from '@/core/features/a-component/hooks/use-component-pagination';
import SpotResultsDesktopV4LoadingTile from '@/features/spot-results/spot-results-desktop-v4/spot-results-desktop-v4-loading-tile';
import SpotResultsTileSwitchDesktop from '@/features/spot-results/spot-results-desktop-v4/spot-results-tile-switch-desktop';

export type SpotResultsDesktopV4Props = PageComponentPropsWithComponent<
    SpotResultsDesktopV4_jsonld_page_read,
    SpotResultDesktopV4Meta
>;

export default function SpotResultsDesktopV4({ component, componentIri, componentType }: SpotResultsDesktopV4Props) {
    const componentPagination = useComponentPagination<SpotResultsDesktopV4_jsonld_page_read>(
        'SpotResultsDesktopV4',
        componentIri,
        componentIri,
    );

    const { fetchNextPage, hasNextPage, pages } = componentPagination;

    const { isHorizontalOrientation, spotTiles } = React.useMemo(() => {
        if (component.loading === 'partial') {
            return {
                isHorizontalOrientation: component.attributes.tiles.some(
                    (tile) => tile.type === 'spotResultHorizontal',
                ),
                spotTiles: component.attributes.tiles,
            };
        }

        const spotTiles = pages.flatMap((page) => page.attributes.tiles);
        return {
            isHorizontalOrientation: spotTiles.some((tile) => tile.type === 'spotResultHorizontal'),
            spotTiles,
        };
    }, [component.attributes.tiles, component.loading, pages]);

    const hasTopLivePrices = spotTiles.some(
        (tile) => tile.type === 'spotResult' && tile.topLivePrices.some((topLivePrice) => !!topLivePrice.price),
    );

    return (
        <>
            <div className={'flex relative flex-col gap-15 w-full'}>
                <>
                    {spotTiles.map((tile, index) => {
                        if (!tile) {
                            logger.error('Error in SpotResultsDesktopV4 Tile. Tile is undefined or null', {
                                additionalData: JSON.stringify({
                                    component,
                                    componentPagination,
                                }),
                            });
                            return null;
                        }
                        const minHeight = (() => {
                            if (tile.type === 'infoBox') {
                                return 283;
                            }
                            if (tile.type === 'infoText') {
                                return 48;
                            }
                            if (tile.type === 'spotResult' && tile.badges?.length > 0) {
                                return 302;
                            }
                            return 302;
                        })();

                        return (
                            <LazyComponent
                                className={'relative'}
                                componentIndex={index}
                                componentKey={getSpotResultTileKey(tile)}
                                componentType={componentType}
                                disableSkeleton={tile.type === 'infoText'}
                                initialInView={index <= 3}
                                key={`${tile.type}-${index}`}
                                minHeight={minHeight}
                            >
                                <>
                                    <SpotResultsTileSwitchDesktop {...tile} />
                                    {hasNextPage && index === spotTiles.length - 1 && (
                                        <InView
                                            onChange={(inView) => (inView ? fetchNextPage() : undefined)}
                                            rootMargin={'1000px'}
                                            threshold={0}
                                        />
                                    )}
                                </>
                            </LazyComponent>
                        );
                    })}
                    {hasNextPage && (
                        <SpotResultsDesktopV4LoadingTile
                            hasTopLivePrices={hasTopLivePrices}
                            isHorizontalOrientation={isHorizontalOrientation}
                        />
                    )}
                </>
            </div>
        </>
    );
}

function getSpotResultTileKey(tile: SpotResultsTileDesktopAttributes): number | string {
    switch (tile.type) {
        case 'spotResult':
            return tile.id;
        case 'spotResultHorizontal':
            return tile.tiles.reduce((acc, tile) => acc + tile.id, '');
        case 'infoBox':
            return tile.title;
        case 'infoText':
            return tile.htmlText;
    }
}
